import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@thd-olt-component-react/router';
import { useConfigService } from '@thd-nucleus/experience-context';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants/payments-table';
import { PAYMENTS_PAGE_SIZE } from '../../../constants/general';
import { CheckingAccountList } from '../checking-account-list/checking-account-list';
import { PAYMENT_FORM_MODE } from '../../../models/common/payments';
import { usePayments } from '../../../hooks/use-payments';
import { CreditCardList } from '../credit-card-list/credit-card-list';
import { ProXtraDollarsList } from '../proxtra-dollars-list/proxtra-dollars-list';
import { GiftCardListV2 } from '../gift-card-list/gift-card-list-v2';
import { KeytagsList } from '../keytags-list/keytags-list';
import { CardType } from '../../../constants/payments';
import { EditCreditCardForm } from '../edit-credit-card-form/edit-credit-card-form';
import { PayPalList } from '../paypal-list/paypal-list';
import {
  setPaymentsPageAnalytics,
  setTenderTypeSelectionAnalytics,
  setPTCSuccessAuthorizationAnalytics,
} from '../../../utils/payments-analytics';
import { RemotePurchases } from '../remote-purchases-auth/remote-purchases-auth';
import { HDWalletAuth } from '../hd-wallet-auth/hd-wallet-auth';
import { useLoyaltyMembershipDetails } from '../../../hooks/use-loyalty-membership-details';
import { HDPassOrangePayContainer } from '../hd-wallet-auth/hd-wallet-container-orange-pay';
import { RemotePurchasesOrangePayContainer } from '../remote-purchases-auth/remote-purchases-orange-pay-container';
import { ProHouseAccountAuthForm } from '../pro-house-accounts-auth-form/pro-house-accounts-auth-form';
import { ProHouseAccountList } from '../pro-house-account-list/pro-house-account-list';
import { TenderTypeButton } from '../../shared/tender-type-button/tender-type-button';
import { scrollToTop } from '../../../utils/general';
import {
  CustomerRole,
  TCustomerRole,
  TCustomerType,
} from '../../../models/common/customer';
import { hasBillingAddress } from '../../../utils/payments';
import { useSearchParam } from '../../../hooks/use-search-param';
import { TAlertStatus } from '../../../models/common/alert-messages';
import { TProHouseAccountAction } from '../../../models/b2b/payments';

type TenderTypeConfig = {
  type: CardType;
  title: string;
  pageComponent: React.FunctionComponent<{
    customerType: TCustomerType;
    customerRole: TCustomerRole;
  }>;
  shouldShow: (params: {
    isB2B: boolean;
    customerRole: TCustomerRole;
    shouldShowProXtraDollars: boolean;
    shouldShowPHA: boolean;
    shouldShowSavedGiftCardB2c: boolean;
  }) => boolean;
};

const tenderTypesConfig: TenderTypeConfig[] = [
  {
    type: CardType.CREDIT_CARD,
    title: 'Credit Cards',
    pageComponent: CreditCardList,
    shouldShow: () => true,
  },
  {
    type: CardType.PRO_XTRA_DOLLARS,
    title: 'Pro Xtra Dollars',
    pageComponent: ProXtraDollarsList,
    shouldShow: ({ isB2B, shouldShowProXtraDollars }) =>
      isB2B && shouldShowProXtraDollars,
  },
  {
    type: CardType.GIFT_CARD,
    title: 'Gift Cards',
    pageComponent: GiftCardListV2,
    shouldShow: ({ isB2B, shouldShowSavedGiftCardB2c }) =>
      isB2B || shouldShowSavedGiftCardB2c,
  },
  {
    type: CardType.PAYPAL_ACCOUNT,
    title: 'PayPal',
    pageComponent: PayPalList,
    shouldShow: () => true,
  },
  {
    type: CardType.CHECKING_ACCOUNT,
    title: 'Checking Accounts',
    pageComponent: CheckingAccountList,
    shouldShow: ({ isB2B }) => isB2B,
  },
  {
    type: CardType.KEY_TAG,
    title: 'Keytags',
    pageComponent: KeytagsList,
    shouldShow: ({ isB2B, customerRole }) =>
      isB2B && customerRole === CustomerRole.ADMIN,
  },
  {
    type: CardType.PRO_HOUSE_ACCOUNT,
    title: 'Pro Trade Credit',
    pageComponent: ProHouseAccountList,
    shouldShow: ({ isB2B, shouldShowPHA, customerRole }) =>
      isB2B && customerRole === CustomerRole.ADMIN && shouldShowPHA,
  },
];

export const PaymentsContent = ({
  customerType,
  customerRole,
}: {
  customerType: TCustomerType;
  customerRole: TCustomerRole;
}) => {
  const isConfigurableCardsEnabled = !!useConfigService(
    'isConfigurableCardsEnabled',
  );
  const isHDPassOrangePayEnabled = !!useConfigService(
    'MICRO_UI_HD_PASS_ENABLED',
  );
  const isRemotePurchasesOrangePayEnabled = !!useConfigService(
    'MICRO_UI_REMOTE_PURCHASES_ENABLED',
  );

  const {
    getPaymentCards,
    creditCardForm,
    dispatchCreditCardForm,
    isListView,
    setAlertMessage,
  } = usePayments();

  const navigate = useNavigate();
  const isB2B = customerType === 'B2B';
  const isAdmin = customerRole === CustomerRole.ADMIN;
  const isSaveGiftCardToAccountB2bEnabled = !!useConfigService(
    'fs-prop:payments__enableSaveGiftCardToAccountB2b',
  );
  const isSaveGiftCardToAccountB2cEnabled = !!useConfigService(
    'fs-prop:payments__enableSaveGiftCardToAccountB2c',
  );
  const shouldShowSavedGiftCard = isB2B
    ? isSaveGiftCardToAccountB2bEnabled
    : isSaveGiftCardToAccountB2cEnabled;

  const { data, hasProTradeCredit, loading } = useLoyaltyMembershipDetails();

  const shouldShowProXtraDollars = useMemo(() => {
    if (!data) return false;
    return (
      !!data.loyaltyMembershipDetails?.customerInfo?.enrolledInPerksProgram &&
      isAdmin
    );
  }, [data, isAdmin]);

  const availableTenders = tenderTypesConfig.filter(({ shouldShow }) => {
    return shouldShow({
      isB2B,
      customerRole,
      shouldShowPHA: hasProTradeCredit,
      shouldShowProXtraDollars,
      shouldShowSavedGiftCardB2c: isSaveGiftCardToAccountB2cEnabled,
    });
  });

  const selectedPaymentTab = useSearchParam('selectedTab');
  const determineNewTab = (): CardType => {
    if (!selectedPaymentTab) {
      return CardType.CREDIT_CARD;
    }
    // Only resolve to a tab the current user is actually allowed to see -
    // otherwise a direct/stale link to a role-gated tab (e.g. Keytags for a
    // non-Admin) selects it anyway and triggers an unnecessary fetch for
    // content that will never render.
    const availableTab = availableTenders.find(({ type }) => {
      return type === selectedPaymentTab;
    });
    return availableTab ? availableTab.type : CardType.CREDIT_CARD;
  };

  const [selectedTenderType, setSelectedTenderType] = useState(() =>
    determineNewTab(),
  );

  useEffect(() => {
    setPaymentsPageAnalytics();
  }, []);

  useEffect(() => {
    const type = determineNewTab();
    setSelectedTenderType(type);

    const pageSize =
      type === CardType.CREDIT_CARD && isListView
        ? DEFAULT_ROWS_PER_PAGE
        : PAYMENTS_PAGE_SIZE;

    // this prevents API call from occurring for gift cards
    if (shouldShowSavedGiftCard && type === CardType.GIFT_CARD) {
      return;
    }

    if (!loading) {
      getPaymentCards(type, false, hasProTradeCredit, {
        pageSize,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentTab, loading, hasProTradeCredit]);

  const onSwitchTenderTypes = (type: CardType) => {
    if (type === selectedTenderType) return;

    if (type !== CardType.GIFT_CARD) {
      setTenderTypeSelectionAnalytics(type);
    }
    setSelectedTenderType(type);
    const params = new URLSearchParams(window.location.search);
    params.set('selectedTab', type);
    navigate({
      search: `?${params.toString()}`,
    });
  };

  if (
    creditCardForm.mode === PAYMENT_FORM_MODE.REMOTE_PURCHASES_CREDIT_CARD &&
    selectedTenderType === CardType.CREDIT_CARD &&
    creditCardForm.card
  ) {
    return isRemotePurchasesOrangePayEnabled ? (
      <RemotePurchasesOrangePayContainer
        onCloseForm={() => {
          dispatchCreditCardForm({ type: 'close' });
          getPaymentCards(selectedTenderType, false, hasProTradeCredit);
        }}
        card={creditCardForm.card}
      />
    ) : (
      <RemotePurchases
        isConfigurableCardsEnabled={isConfigurableCardsEnabled}
        card={creditCardForm.card}
        onCloseForm={() => {
          dispatchCreditCardForm({ type: 'close' });
          getPaymentCards(selectedTenderType, false, hasProTradeCredit);
        }}
        fromEditCard={
          creditCardForm.origin === PAYMENT_FORM_MODE.EDIT_CREDIT_CARD
        }
      />
    );
  }

  if (
    creditCardForm.card &&
    creditCardForm.mode === PAYMENT_FORM_MODE.EDIT_CREDIT_CARD &&
    selectedTenderType === CardType.CREDIT_CARD
  ) {
    return (
      <EditCreditCardForm
        card={creditCardForm.card}
        onCancelForm={() => {
          dispatchCreditCardForm({ type: 'close' });
        }}
        onEditSuccess={() => {
          dispatchCreditCardForm({ type: 'close' });

          getPaymentCards(selectedTenderType, false, hasProTradeCredit).then(
            (paymentCards) => {
              if (
                creditCardForm.origin ===
                PAYMENT_FORM_MODE.REMOTE_PURCHASES_CREDIT_CARD
              ) {
                const newCard = paymentCards.find(
                  (commonCard) =>
                    commonCard.paymentId === creditCardForm.card?.paymentId,
                );
                // if edit card succeeds, open the remote purchases form
                dispatchCreditCardForm({
                  type: 'open',
                  mode: PAYMENT_FORM_MODE.REMOTE_PURCHASES_CREDIT_CARD,
                  card: newCard || creditCardForm.card,
                  origin: PAYMENT_FORM_MODE.EDIT_CREDIT_CARD,
                });
              }
            },
          );
        }}
        fromRemotePurchases={
          creditCardForm.origin ===
          PAYMENT_FORM_MODE.REMOTE_PURCHASES_CREDIT_CARD
        }
      />
    );
  }

  if (
    creditCardForm.mode === PAYMENT_FORM_MODE.AUTH_HD_WALLET_CREDIT_CARD &&
    selectedTenderType === CardType.CREDIT_CARD &&
    creditCardForm.card
  ) {
    return isHDPassOrangePayEnabled &&
      hasBillingAddress(creditCardForm.card) ? (
      <HDPassOrangePayContainer
        card={creditCardForm.card}
        onCloseForm={() => {
          dispatchCreditCardForm({ type: 'close' });
        }}
        onSuccess={() => {
          dispatchCreditCardForm({ type: 'close' });
          getPaymentCards(selectedTenderType, false, hasProTradeCredit);
        }}
      />
    ) : (
      <HDWalletAuth
        card={creditCardForm.card}
        onCloseForm={() => {
          dispatchCreditCardForm({ type: 'close' });
        }}
        onSuccess={() => {
          dispatchCreditCardForm({ type: 'close' });
          getPaymentCards(selectedTenderType, false, hasProTradeCredit);
        }}
        canUseCardDetailsForm={false}
      />
    );
  }

  if (
    creditCardForm.card &&
    creditCardForm.mode ===
      PAYMENT_FORM_MODE.REMOTE_PURCHASES_PRO_HOUSE_ACCOUNT &&
    selectedTenderType === CardType.PRO_HOUSE_ACCOUNT
  ) {
    return (
      <ProHouseAccountAuthForm
        card={creditCardForm.card}
        onCloseForm={() => {
          scrollToTop();
          dispatchCreditCardForm({ type: 'close' });
        }}
        onSuccess={(
          action: TProHouseAccountAction,
          message: React.ReactChild,
        ) => {
          setPTCSuccessAuthorizationAnalytics({
            action,
            locType: creditCardForm.card?.locType,
            t2cPrimary: creditCardForm.card?.t2cPrimary || false,
          });
          setAlertMessage({
            paymentId: creditCardForm.card?.paymentId,
            alertStatus: TAlertStatus.SUCCESS,
            text: message,
          });
          scrollToTop();
          dispatchCreditCardForm({ type: 'close' });
          getPaymentCards(selectedTenderType, false, hasProTradeCredit);
        }}
        onFailure={(
          action: TProHouseAccountAction,
          message: React.ReactChild,
        ) => {
          setAlertMessage({
            paymentId: creditCardForm.card?.paymentId,
            alertStatus: TAlertStatus.ERROR,
            text: message,
          });
          scrollToTop();
          dispatchCreditCardForm({ type: 'close' });
          getPaymentCards(selectedTenderType, false, hasProTradeCredit);
        }}
      />
    );
  }

  return (
    <>
      <div
        className={`sui-grid ${
          !isB2B ? 'sui-grid-cols-2' : 'sui-grid-cols-3'
        } sui-gap-y-4 sui-gap-x-4 md:sui-gap-x-8 md:sui-gap-y-6 md:sui-grid-cols-3 lg:sui-grid-cols-2 2xl:sui-grid-cols-3`}
      >
        {availableTenders.map(({ title, type }) => {
          return (
            <TenderTypeButton
              dataTestid={`${type}-modal-btn`}
              key={type}
              fullWidth
              selected={type === selectedTenderType}
              onClick={() => {
                onSwitchTenderTypes(type);
              }}
            >
              {title}
            </TenderTypeButton>
          );
        })}
      </div>
      <div className="sui-mt-6 md:sui-mt-10">
        {availableTenders.map(
          (config) =>
            config.type === selectedTenderType && (
              <config.pageComponent
                key={config.type}
                customerRole={customerRole}
                customerType={customerType}
              />
            ),
        )}
      </div>
    </>
  );
};
